// @flow

import {useState} from 'react';
import _ from 'lodash';
import {Button, Spinner, Menu, MenuItem} from '@blueprintjs/core';
import {Popover2 as Popover} from "@blueprintjs/popover2";
import {Desktop, Mobile} from '../layouts/devices-sizes';
import {
  trackEvent,
  DOWNLOAD_CSV_BY_DAY_BUTTON,
} from '../../services/mixpanel';

import * as ActiveMetricsModel from '../../models/active-metrics';
import * as LocationModel from '../../models/location';
import * as QueryResponseListModel from '../../models/query-response-list';
import * as AvailableMetricsModel from '../../models/available-metrics';
import * as QueryApiRequestModel from '../../models/query-api-request';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import {generateCSVFromSQLQuery} from '../../services/sql-api';
import {errorToast} from '../../utils/toaster';
import type {locationConfigT} from './summary-btn';
import {
  GENDERS,
  ROLES,
  SORTED_AGE_KEYS,
} from '../../constants/demographic-types';
import {apiEndpointByMetricKey} from '../../constants/api-endpoints';
import {CSV_GEN_PAYLOAD_TYPES} from '../../services/query-api';

type Props = {
  disabled: boolean,
  locations: LocationModel.t[],
  queryResponseList: QueryResponseListModel.t,
  activeMetrics: ActiveMetricsModel.t,
  filteredLocationIds: string[],
  availableMetrics: AvailableMetricsModel.t,
  period: QueryApiRequestModel.periodT,
  filteredLocationsConfigList: locationConfigT[],
  breakdownBy?: string,
  showAgeAndGenderFilters: boolean,
  showStaffFilters: boolean,
  excludeStaff: boolean,
  locations: LocationModel.t[],
};

type ActiveMetricListItem = {
  label: string,
  metricKey: string,
  metricGroupKey: string,
  taxonomies: string[] | null,
};

const baseColumns = [
  {
    heading: 'Local Time',
    fetchValue: 'index',
  },
  {
    heading: 'Location Name',
    fetchValue: 'location_name',
  },
];
const baseSortBy = [1, 0];

const DownloadByDayCSV = (
  {
    disabled,
    activeMetrics,
    filteredLocationIds,
    period,
    breakdownBy,
    showAgeAndGenderFilters,
    showStaffFilters,
    excludeStaff,
    locations,
    availableMetrics,
  }: Props) => {
  const [loading, setLoading] = useState(false);

  const onDownloadByDay = async (
    split: 'age' | 'gender' | 'age&gender' | 'role' | 'none'
  ) => {
    setLoading(true);
    try {
      // Format times
      const start = moment.utc(period.start).toISOString();
      const end = moment.utc(period.end);
      end.add(1, 'day');
      end.subtract(1, 'second');
      const csvFormat = {
        sortBy: [...baseSortBy],
        rowFilters: ['index', 'location'],
        columns: [...baseColumns],
      };
      const metricRequests = [];

      const activeMetricsList: ActiveMetricListItem[] = [];

      // Format the active metrics list
      Object.entries(activeMetrics).forEach(
        ([metricGroupKey, metricGroup]: [string, Object]) =>
          metricGroup.metrics.forEach((metric) => {
            const metricDetails = AvailableMetricsModel.findMetricDetails(
              availableMetrics,
              metricGroupKey,
              metric,
            );

            const metricLabel =
              AvailableMetricsModel.getMetricLabel(metricDetails) || metric;

            if (metricGroup.taxonomies || metricGroup.filters) {
              let taxonomies = metricGroup.taxonomies;
              if (metricGroup.filters) {
                taxonomies = metricGroup.filters.map(
                  (filter) => `${filter.key}:${filter.value}`,
                );
              }

              activeMetricsList.push(
                ...taxonomies.map((taxonomy) => ({
                  label: `${metricLabel} - ${taxonomy}`,
                  metricGroupKey,
                  metricKey: metric,
                  taxonomies: [taxonomy],
                })),
              );
            } else {
              activeMetricsList.push({
                label: metricLabel,
                metricGroupKey,
                metricKey: metric,
                taxonomies: null,
              });
            }
          }),
      );

      // Build multiple requests for each active metric
      // TODO: move this logic server side - it will be more efficient and make it simpler for the API end users

      activeMetricsList.forEach((item) => {
        // Base Metric Query -- need to update Breakdowns, metricId, name and roles.
        const baseQuery: {
          start: string,
          end: string,
          aggregationPeriod: string,
          facets: string[],
          entityType: string,
          insideRecordingTimes: boolean,
          roles?: string[],
          entities?: string[],
          name?: string,
          metricId?: string,
          taxonomy?: string[],
          breakdownByDimensions?: string[],
          ages?: string[],
          genders?: string[],
          areaType?: string,
          lineType?: string,
        } = {
          start,
          end: end.toISOString(),
          aggregationPeriod: 'day',
          facets: ['segments'],
          entityType: 'location',
          insideRecordingTimes: true,
          entities: locations.map((loc) => loc.id),
        };

        if (locations.length !== filteredLocationIds.length) {
          baseQuery.entities = filteredLocationIds;
        }

        if (item.metricKey) {
          baseQuery.name = apiEndpointByMetricKey(item.metricKey);
        }

        if (item.metricGroupKey) {
          baseQuery.metricId = item.taxonomies
            ? `${item.metricKey}_${item.taxonomies.join('_')}`
            : item.metricKey;
        }

        if (item.taxonomies) {
          baseQuery.taxonomy = item.taxonomies;

          if (
            [
              CSV_GEN_PAYLOAD_TYPES.AREAS,
              CSV_GEN_PAYLOAD_TYPES.LOCATION_AREAS,
              CSV_GEN_PAYLOAD_TYPES.SERVICE_AREAS,
            ].includes(item.metricGroupKey)
          ) {
            baseQuery.areaType = 'taxonomy';
          }
        }

        // Generate metric query column 1 instead of many
        const baseMetricColumn: {
          heading: string,
          field: string,
          filter?: { taxonomy: string },
        } = {
          heading: item.label,
          field: item.metricKey,
          metricId: item.taxonomies
            ? `${item.metricKey}_${item.taxonomies.join('_')}`
            : item.metricKey,
        };

        if (baseQuery.taxonomy) {
          baseQuery.taxonomy
            ? (baseMetricColumn.filter = {taxonomy: baseQuery.taxonomy[0]})
            : (baseMetricColumn.filter = {});
        }

        csvFormat.columns.push(baseMetricColumn);

        // Generate metric queries for row filters, default: age and gender
        // Transactions cannot be broken down by age or gender
        const breakdownByDimensions =
          baseQuery.taxonomy && baseQuery.taxonomy.length
            ? ['entity', 'taxonomy']
            : ['entity'];

        const queryVariants = [];

        if (item.metricGroupKey !== CSV_GEN_PAYLOAD_TYPES.SALES) {

          if (split === 'none' && showStaffFilters && excludeStaff) {
            baseQuery.roles = ['customer']
          } else if (split !== 'none') {
            baseQuery.roles = ['customer']
          }

          if (split && split.includes('age')) {
            queryVariants.push(cloneDeep({
              ...baseQuery, breakdownByDimensions: [...breakdownByDimensions, 'age'], ages: SORTED_AGE_KEYS, roles: ['customer']
            }))
            if (!csvFormat.rowFilters.includes('age')) csvFormat.rowFilters.push('age');
          }

          if (split && split.includes('gender')) {
            queryVariants.push(cloneDeep({
              ...baseQuery, breakdownByDimensions: [...breakdownByDimensions, 'gender'], genders: GENDERS, roles: ['customer']
            }))
            if (!csvFormat.rowFilters.includes('gender')) csvFormat.rowFilters.push('gender');
          }

          if (split === 'age&gender') {
            queryVariants.push(cloneDeep({
              ...baseQuery, breakdownByDimensions: [...breakdownByDimensions, 'age', 'gender'], ages: SORTED_AGE_KEYS, genders: GENDERS, roles: ['customer']
            }))
            if (!csvFormat.rowFilters.includes('age')) csvFormat.rowFilters.push('age');
            if (!csvFormat.rowFilters.includes('gender')) csvFormat.rowFilters.push('gender');
          }

          if (split === 'role' || (split !== 'none' && showStaffFilters && !excludeStaff)) {
            queryVariants.push(cloneDeep({
              ...baseQuery, breakdownByDimensions: [...breakdownByDimensions, 'role'], roles: ROLES
            }))
            if (!csvFormat.rowFilters.includes('role')) csvFormat.rowFilters.push('role');
          }
        }

        // Add metric requests to csv format
        metricRequests.push(...[
          cloneDeep({...baseQuery, breakdownByDimensions: [...breakdownByDimensions]}),
          ...queryVariants,
        ]);
      });

      // Send request to SQL API
      const response = await generateCSVFromSQLQuery({
        csvFormat,
        metricRequests,
      });

      if (response && response.body) {
        const signedUrl = response.body.filter((o) => o.signedUrl)[0].signedUrl;

        const a = document.createElement('a');
        a.href = signedUrl;
        a.click();
      } else {
        errorToast({message: 'Error generating CSV', timeout: 2500});
      }
      trackEvent(DOWNLOAD_CSV_BY_DAY_BUTTON);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Popover
      placement="bottom"
      interactionKind='click'
      minimal={true}
      disabled={disabled || loading}
      content={
        <Menu>
          {showStaffFilters ? <MenuItem
            text="Split by role"
            onClick={() => {
              onDownloadByDay('role')
            }}
          /> : null
          }
          {showAgeAndGenderFilters ? <MenuItem
            text="Split by gender"
            onClick={() => {
              onDownloadByDay('gender')
            }}
          /> : null
          }
          {showAgeAndGenderFilters ? <MenuItem
            text="Split by age"
            onClick={() => {
              onDownloadByDay('age')
            }}
          /> : null
          }
          {showAgeAndGenderFilters ? <MenuItem
            text="Split by age and gender"
            onClick={() => {
              onDownloadByDay('age&gender')
            }}
          /> : null
          }
          <MenuItem
            text={`No split${!showStaffFilters? '' : excludeStaff? ' (excluding staff)' : ' (including staff)'}`}
            onClick={() => {
              onDownloadByDay('none')
            }}
          />
        </Menu>
      }
    >
      <Button
        disabled={disabled || loading}
        icon={loading ? <Spinner size={18}/> : 'floppy-disk'}
        rightIcon="caret-down"
      >
        <Desktop>
          {loading ? 'Loading...' : 'Export visible data by day (CSV)'}
        </Desktop>
        <Mobile>
          {loading ? 'Loading...' : 'Export visible data by day (CSV)'}
        </Mobile>
      </Button>
    </Popover>
  );
};

export default DownloadByDayCSV;
